import React, { useState, useEffect, useCallback } from "react";
import PublicHeader from "../components/PublicHeader";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/EventPage.css";
import SiteMenu from "../components/SiteMenu";
import AlbumCard from "../components/AlbumCard";
import PhotoGrid from "../components/PhotoGrid";
import { Event } from "../types/Event";
import { EventService } from "../services/EventService";
import { useAuth } from "../context/AuthContext";

const EventPage: React.FC = () => {
  const activities = [
    { label: "Appel d'offre", path: "offre" },
    { label: "Projet", path: "/project" },
    { label: "Calendrier", path: "/calendar" },
    { label: "Événements", path: "/event" },
  ];

  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const { api, user } = useAuth();

  // ----- Récupération des événements et de leurs images -----
  const fetchEventsWithImages = useCallback(async () => {
    try {
      const data = await EventService.getAllEvents(api);

      const eventsWithImages = await Promise.all(
        data.map(async (ev) => {
          const images = await EventService.getAllImgByEvent(api, ev.id_event);

          const imagesWithFullPath = images?.map(img => ({
            ...img,
            path: img.path,
          })) || [];

          return {
            ...ev,
            images: imagesWithFullPath,
          };
        })
      );

      setEvents(eventsWithImages);
    } catch (error) {
      console.error("Erreur lors de la récupération des événements :", error);
    }
  }, [api]);

  useEffect(() => {
    fetchEventsWithImages();
  }, [fetchEventsWithImages]);

  // ----- Ajouter une photo -----
  const handleAddPhoto = async (eventId: number, file: File | null) => {
    if (!file) return;

    try {
      await EventService.uploadEventImg(api, eventId, file);

      // Après upload, refetch pour mettre à jour toutes les images
      fetchEventsWithImages();
    } catch (error) {
      console.error("Erreur lors de l'ajout de la photo :", error);
    }
  };

  return (
    <div className="project-page-wrapper">
      <div className="map-page-header mb-3">
        <PublicHeader />
      </div>

      <div className="container-fluid">
        <div className="row">
          <div className="col-12 col-md-3 col-lg-2 mb-3 mb-md-0">
            <SiteMenu activities={activities} />
          </div>

          <main className="col-12 col-md-9 col-lg-10 project-page">
            {selectedEvent === null ? (
              <>
                <h2>Événements</h2>
                <div className="album-grid">
                  {events.map((ev) => (
                    <AlbumCard
                      key={ev.id_event}
                      event={ev}
                      onClick={() => setSelectedEvent(ev)}
                      onAddPhoto={handleAddPhoto}
                      roleRank={user?.roleRank}
                    />
                  ))}
                </div>
              </>
            ) : (
              <>
                <button
                  className="btn btn-secondary mb-3"
                  onClick={() => setSelectedEvent(null)}
                >
                  ← Retour
                </button>

                <h2>{selectedEvent.titre}</h2>
                <p>
                  <strong>Date : </strong>
                  {new Date(selectedEvent.start_date).toLocaleDateString("fr-FR", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  })}
                  {selectedEvent.end_date && (
                    <> - {new Date(selectedEvent.end_date).toLocaleDateString("fr-FR", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    })}</>
                  )}
                </p>
                <p>
                  <strong>Lieu : </strong>{selectedEvent.lieu || "Non spécifié"}
                </p>
                {selectedEvent.description && <p>{selectedEvent.description}</p>}

                <PhotoGrid
                  photos={selectedEvent.images?.map((i) => i.path) || []}
                  onAddPhoto={() => handleAddPhoto(selectedEvent.id_event, null)}
                />
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default EventPage;
